import React from 'react';
import { DiscussionMessage } from '../domain/types';

interface DiscussionCoreProps {
  title?: string;
  messages: DiscussionMessage[];
  loading?: boolean;
  error?: string | null;
  draft: string;
  sending?: boolean;
  currentUserId?: string;
  onDraftChange: (value: string) => void;
  onSend: () => Promise<void> | void;
}

const formatTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DiscussionCore: React.FC<DiscussionCoreProps> = ({
  title = 'Discussion',
  messages,
  loading = false,
  error,
  draft,
  sending = false,
  currentUserId,
  onDraftChange,
  onSend,
}) => {
  return (
    <div style={{ border: '1px solid #dfe8f8', borderRadius: 14, background: '#fff', display: 'flex', flexDirection: 'column', minHeight: 520, height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid #edf2fb', fontWeight: 700, color: '#173159' }}>
        {title}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'grid', gap: 12, alignContent: loading || messages.length === 0 ? 'center' : 'start' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#64748b' }}>Loading discussion...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', color: '#b91c1c' }}>{error}</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', display: 'grid', gap: 6 }}>
            <div style={{ fontWeight: 700, color: '#334155' }}>No messages yet</div>
            <div style={{ fontSize: 13 }}>Start the discussion.</div>
          </div>
        ) : messages.map((message) => {
          const isCurrentUser = currentUserId ? message.senderId === currentUserId : false;
          return (
            <div key={message.id} style={{ display: 'flex', justifyContent: isCurrentUser ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '88%', borderRadius: 12, padding: '10px 12px', background: isCurrentUser ? '#e0edff' : '#f8fafc', border: `1px solid ${isCurrentUser ? '#bfdbfe' : '#e2e8f0'}`, opacity: message.pending ? 0.7 : 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#173159' }}>{message.senderDisplayName}</span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{formatTime(message.createdAt)}</span>
                </div>
                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.45, color: '#0f172a', fontSize: 14 }}>{message.text}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ borderTop: '1px solid #edf2fb', padding: 12, display: 'grid', gap: 8, background: '#fbfdff' }}>
        <textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          placeholder="Write a message..."
          rows={3}
          style={{ width: '100%', resize: 'none', border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="amd-btn amd-btn-primary"
            disabled={sending || !draft.trim()}
            onClick={() => { void onSend(); }}
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscussionCore;
